/* eslint-disable max-len */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import proptypes from 'prop-types';
import { FitCompatibility } from '@thd-olt-component-react/fit-compatibility';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  DrawerHeader,
  DrawerBody,
  TileGroup
} from '@one-thd/sui-atomic-components';
import {
  getRefinementsByDimension,
  isRefinementActive,
  sortSelectedDimensions,
  sortRefinementKeys,
  sortAscending,
  findCustomPriceRange,
  getDimensionsForUrl,
  getRefinemntsLabelsForUrl,
  filtersCatStyleCheck,
  sortOrder,
  onRatingRefinements,
  storeLifecycleEvent,
  extractNavParamFromClearAllURL,
  hasFitCompatibility,
  findMatchingPriceRange,
  hasExactPriceRangeMatch,
  isPlpFulfillmentLabel,
  transformFulfillmentDimension
} from '../../product-results-helpers';
import { MultiStateDrawerRefinement } from './MultiStateDrawerRefinement';
import { RatingRefinement } from '../Refinements/RatingRefinement';
import { CustomDrawerFooter } from './CustomDrawerFooter';
import { useSearchModelData } from '../../hooks/useSearchModelData';
import { PriceRangeRefinement } from '../Refinements/PriceRangeRefinement';

const { string, shape, arrayOf, func } = proptypes;

const PrimaryFilterDrawerDimension = ({
  dimension,
  appliedDimensions,
  onCancel,
  onDimensionChange,
  baseUrl,
  canonicalUrl,
  onRefinementsUpdate
}) => {

  const isPlpSpeedEnabled = useConfigService('fs-prop:isPlpSpeedEnabled');
  const { data } = useSearchModelData();
  const resultsAppliedDimensions = data?.searchModel?.appliedDimensions || [];

  const footerLabel = resultsAppliedDimensions.filter((item) => item.label === dimension.label).length > 0;

  const activeSelectedRefinements = appliedDimensions ? getRefinementsByDimension(appliedDimensions, dimension) : [];
  const [selectedRefinements, setSelectedRefinements] = useState(activeSelectedRefinements);

  const [selectedRefinementData, setSelectedRefinementData] = useState({
    label: null,
    refinementKey: null
  });

  const [revision, setRevision] = useState(0);
  const [customRangeEntered, setCustomRangeEntered] = useState(0);

  const [lowerBound, setLowerBound] = useState('');
  const [upperBound, setUpperBound] = useState('');

  const isLowerBoundEntered = (value) => setLowerBound(value);

  const isUpperBoundEntered = (value) => setUpperBound(value);

  const desktopSort = useRef('');

  const handleSortOrderChange = ({ output: result }) => {
    if (result?.sortby && result?.sortorder) {
      desktopSort.current = `${result.sortby}:${result.sortorder}`;
    }
  };

  useEffect(() => {
    setSelectedRefinements(getRefinementsByDimension(appliedDimensions, dimension));
    window.LIFE_CYCLE_EVENT_BUS.on('sortorder.value', handleSortOrderChange);
  }, [appliedDimensions, desktopSort]);

  const onRefinementChange = ({ refinement }) => {
    setSelectedRefinementData({
      label: refinement.label,
      refinementKey: refinement.refinementKey
    });

    // Handle custom price range: replace existing refinements and exit early
    if (refinement?.dimension?.dimensionId === 'custom_price') {
      const { refinements } = dimension;
      const priceRangeMatches = findMatchingPriceRange(refinement.lowerbound, refinement.upperbound, refinements);
      if (priceRangeMatches.length > 0 && hasExactPriceRangeMatch(lowerBound, upperBound, refinements)) {
        setSelectedRefinements([...priceRangeMatches]);
        return;
      }
      setSelectedRefinements([refinement]);
      return;
    }
    // verify whether or not the refinement is on the list
    const index = selectedRefinements.findIndex((ref) => ref.refinementKey === refinement?.refinementKey);

    // copy current list
    let temp = Array.isArray(selectedRefinements) ? selectedRefinements.slice() : [];
    if (index >= 0) {
      // if already in the list, remove it
      temp.splice(index, 1);
    } else {
      // if not in the list, add it
      temp.push(refinement);
    }
    setSelectedRefinements(temp);
  };

  const handleStarRatingChange = ({ refinement }) => {
    setSelectedRefinements(onRatingRefinements(refinement));
  };

  const clearInputFields = () => {
    setLowerBound('');
    setUpperBound('');
    setRevision(revision + 1);
  };

  const processAppliedDimensions = (currentDimension, currentRefinements, existingDimensions) => {
    const updatedTemp = existingDimensions.slice();
    const index = existingDimensions.findIndex((ref) => ref.label === currentDimension.label);

    if (index !== -1) {
      updatedTemp[index] = { ...updatedTemp[index], refinements: [...currentRefinements] };
    } else {
      updatedTemp.push({ label: currentDimension.label, refinements: selectedRefinements });
    }

    return updatedTemp;
  };

  const handleOnApply = useCallback(() => {
    document.body.classList.add('sui-opacity-50');
    setTimeout(() => {

      const selectedDimensions = processAppliedDimensions(dimension, selectedRefinements, resultsAppliedDimensions);
      const sortedSelectedDimensions = sortSelectedDimensions(selectedDimensions);

      // get refinement keys and sort them by id
      let refinementKeys = sortedSelectedDimensions
        .flatMap((dim) => dim.refinements)
        .map((ref) => ref.refinementKey);

      let sortedRefinementKeys = sortRefinementKeys(refinementKeys);

      if (selectedDimensions && Object.keys(selectedDimensions).length > 0) {
        const updatedSelectedDimensions = selectedDimensions.map((dim) => {
          if (isPlpSpeedEnabled && isPlpFulfillmentLabel(dim.label)) {
            return transformFulfillmentDimension(dim);
          }
          return dim;
        });
        storeLifecycleEvent('product-results.change-filters-refinements', updatedSelectedDimensions);
      }
      // get dimensions that may go on the url
      let dimensionsIncludedInUrl = getDimensionsForUrl(sortedSelectedDimensions);

      // get refinements from the dimensions which are added to the url
      let refinementsIncludedInUrl = dimensionsIncludedInUrl?.map((_dimension) => [..._dimension.refinements].sort(sortAscending)
      ).flat(1);
      let length = refinementsIncludedInUrl.length;
      let lastRefinement = refinementsIncludedInUrl[length - 1];
      let urlParts = baseUrl?.split('?')[0].split('/');

      // get custom price range
      const priceDimension = selectedDimensions.find((_dimension) => _dimension.label === 'Price');
      const [lowerbound, upperbound] = findCustomPriceRange(priceDimension?.refinements);
      const canonicalUrlParts = canonicalUrl.split('?');
      let queryParams = canonicalUrlParts.length > 1 ? canonicalUrlParts[1] : '';
      if (lowerbound || upperbound) {
        let queries = queryParams.split('&')
          .filter((query) => !query.toLowerCase().includes('lowerbound') && !query.toLowerCase().includes('upperbound'));
        queryParams = queries.join('&');
        queryParams += `&lowerbound=${lowerbound}&upperbound=${upperbound}`;
      }

      queryParams = filtersCatStyleCheck(queryParams);

      queryParams = sortOrder({ desktopSort, queryParams });

      const nValue = urlParts.find((part) => part.startsWith('N-')) || '';
      let refinementsIds = nValue;
      if (sortedRefinementKeys.length > 0) {
        refinementsIds += 'Z' + sortedRefinementKeys.join('Z');
      }

      // get refinement labels for url
      const refinamentsLabelsForUrl = getRefinemntsLabelsForUrl(refinementsIncludedInUrl);
      urlParts.splice(urlParts.indexOf(nValue), 0, ...refinamentsLabelsForUrl);

      urlParts.splice(urlParts.indexOf(nValue), 1, refinementsIds);
      const url = queryParams ? urlParts.join('/') + '?' + queryParams : urlParts.join('/');
      lastRefinement = { ...lastRefinement, url };
      onDimensionChange({ refinement: lastRefinement });
      onCancel(selectedRefinements, 'apply');
      clearInputFields();
      document.body.classList.remove('sui-opacity-50');
    }, 0);
  }, [selectedRefinements, dimension, baseUrl, canonicalUrl,
    lowerBound, upperBound, resultsAppliedDimensions]);

  const navParam = extractNavParamFromClearAllURL(data);

  const hasFitCompatibilityEnabled = hasFitCompatibility(dimension.label);

  const triggerRefinementForCustomRange = () => {
    onRefinementChange({
      refinement: {
        lowerbound: lowerBound,
        upperbound: upperBound,
        urlSearchParams: window.location.search,
        label: `$${lowerBound} - $${upperBound}`,
        deselectUrl: '?',
        refinementKey: null,
        dimension: {
          dimensionId: 'custom_price',
          label: 'Price'
        }
      }
    });
  };

  // add entered custom range into refinements object
  useEffect(() => {
    if (lowerBound.length !== 0 || upperBound.length !== 0) {
      setCustomRangeEntered(1);
      triggerRefinementForCustomRange();
    } else if (lowerBound.length === 0 || upperBound.length === 0) {
      setCustomRangeEntered(0);
      setSelectedRefinements(activeSelectedRefinements);
    }

  }, [lowerBound, upperBound]);

  useEffect(() => {
    onRefinementsUpdate(selectedRefinements);
  }, [selectedRefinements]);

  return (
    <>
      <DrawerHeader data-testid={`drawer-header-${dimension.label}`} title={dimension.label} onClose={() => onCancel(selectedRefinements, 'close')} />
      <DrawerBody data-testid="drawer-body">
        {
          dimension.label === 'Price' && (
            <PriceRangeRefinement
              dimension={dimension}
              onChange={onRefinementChange}
              isLowerBoundEntered={isLowerBoundEntered}
              isUpperBoundEntered={isUpperBoundEntered}
              revision={revision}
              drawer={false}
              multiFacet={false}
              isPrimaryFilter
              onEnterApply={handleOnApply}
              selectedRefinements={selectedRefinements}
            />
          )
        }
        {
          dimension.label === 'Review Rating' && (
            <RatingRefinement
              key={`primary-filters-refinement-${dimension.label}`}
              dimension={dimension}
              onItemSelection={handleStarRatingChange}
              selectedRefinements={selectedRefinements}
              secondaryDrawer={false}
            />
          )
        }
        <div>
          {hasFitCompatibilityEnabled && (
            <div
              role="button"
              tabIndex={0}
              aria-label="fit-compatibility"
              className="sui-mb-3"
            >
              <FitCompatibility
                type="MINI"
                drawerPosition="left"
                imageCacheEnabled
                navParam={navParam}
                data={data}
              />
            </div>
          )}
          {dimension.label !== 'Review Rating' && (

            <TileGroup
              value={selectedRefinementData.label}
              aria-label={`${dimension?.label} Options`}
              orientation="vertical"
              onChange={(_event, selectedRefinement) => {
                if (selectedRefinement) {
                  onRefinementChange({ refinement: selectedRefinement });
                }
              }}
              grow
              key={selectedRefinementData.refinementKey}
            >
              {dimension.refinements.map((refinement) => (
                <MultiStateDrawerRefinement
                  refinement={refinement}
                  key={`primary-filters-refinement-${refinement.refinementKey}`}
                  isRefinementSelected={isRefinementActive(refinement.refinementKey, selectedRefinements)}
                />
              ))}
            </TileGroup>
          )}
        </div>
      </DrawerBody>
      <CustomDrawerFooter
        customRangeEntered={customRangeEntered}
        selectedRefinements={selectedRefinements}
        activeSelectedRefinements={activeSelectedRefinements}
        onApply={handleOnApply}
        onCancel={onCancel}
        footerLabel={footerLabel}
      />
    </>
  );
};

PrimaryFilterDrawerDimension.displayName = 'PrimaryFilterDrawerDimension';
PrimaryFilterDrawerDimension.propTypes = {
  dimension: shape({
    label: string
  }).isRequired,
  appliedDimensions: arrayOf(shape({})).isRequired,
  baseUrl: string.isRequired,
  canonicalUrl: string.isRequired,
  onCancel: func.isRequired,
  onDimensionChange: func.isRequired,
  onRefinementsUpdate: func.isRequired
};

export default PrimaryFilterDrawerDimension;
